"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { useSocket } from "../hooks/useSocket";

export default function Home() {
	const [checks, setChecks] = useState<Record<string, { status: number | string; color: string }>>({});
	const [wsStatus, setWsStatus] = useState<{ status: string; color: string }>({ status: "DISCONNECTED", color: "text-red-400" });
	const { isConnected } = useSocket();

	const endpoints = [
		{ name: "Health API", url: "/api/health" },
		{ name: "Auth Session", url: "/api/auth/session" },
		{ name: "Servers API", url: "/api/servers" },
		{ name: "Users API", url: "/api/users" },
		{ name: "Admin Stats API", url: "/api/admin/stats" },
		{ name: "Analytics API", url: "/api/analytics" },
		{ name: "Permissions API", url: "/api/permissions" },
		{ name: "Roles API", url: "/api/roles" },
		{ name: "Games API", url: "/api/games" },
	];

	useEffect(() => {
		endpoints.forEach((ep) => {
			fetch(ep.url + "?health=true", { method: "GET" }) // Add health parameter for bypass
				.then((res) => {
					// 200 = OK, 401 = OK (Protected but alive), 405 = OK (Method Not Allowed but alive)
					const isAlive = [200, 401, 405].includes(res.status);
					setChecks((prev) => ({
						...prev,
						[ep.name]: {
							status: isAlive ? "ONLINE" : `ERR ${res.status}`,
							color: isAlive ? "text-emerald-400" : "text-red-400",
						},
					}));
				})
				.catch((err) => {
					setChecks((prev) => ({
						...prev,
						[ep.name]: { status: "OFFLINE", color: "text-red-400" },
					}));
				});
		});
	}, []);

	useEffect(() => {
		if (isConnected) {
			setWsStatus({ status: "CONNECTED", color: "text-emerald-400" });
		} else {
			setWsStatus({ status: "DISCONNECTED", color: "text-red-400" });
		}
	}, [isConnected]);

	return (
		<div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex flex-col items-center justify-center p-4 text-white font-sans relative overflow-hidden">
			{/* Background Effects */}
			<div className="absolute top-[-20%] left-[-20%] w-[50%] h-[50%] bg-indigo-600/20 rounded-full blur-[150px] pointer-events-none animate-pulse" />
			<div className="absolute bottom-[-20%] right-[-20%] w-[50%] h-[50%] bg-blue-600/20 rounded-full blur-[150px] pointer-events-none animate-pulse delay-1000" />
			<div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[60%] h-[60%] bg-purple-600/10 rounded-full blur-[200px] pointer-events-none animate-pulse delay-500" />

			{/* Animated Grid Background */}
			<div className="absolute inset-0 bg-[linear-gradient(to_right,#475569_1px,transparent_1px),linear-gradient(to_bottom,#475569_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)] opacity-20" />

			<div className="text-center relative z-10 max-w-2xl w-full">
				{/* Header */}
				<div className="mb-8">
					<div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
						<img src="/images/Logo.svg" alt="LinkNSync Logo" className="w-8 h-8 filter brightness-0 invert" />
					</div>
					<h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-4">LinkNSync Backend</h1>
					<p className="text-slate-400">System Status Overview</p>
				</div>

				{/* API Status Card */}
				<div className="bg-slate-900/80 backdrop-blur-xl border border-white/10 p-6 rounded-2xl shadow-2xl mb-6">
					<h2 className="text-xl font-semibold mb-4 text-slate-200">API Endpoints</h2>
					<div className="space-y-3">
						{endpoints.map((ep) => (
							<div key={ep.name} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
								<span className="text-slate-300 font-medium">{ep.name}</span>
								<div className="flex items-center gap-2">
									<div className={`w-3 h-3 rounded-full ${checks[ep.name]?.status === "ONLINE" ? "bg-emerald-400" : checks[ep.name]?.status?.toString().startsWith("ERR") || checks[ep.name]?.status === "OFFLINE" ? "bg-red-400" : "bg-slate-400"} animate-pulse`}></div>
									<span className={`font-mono text-sm font-bold ${checks[ep.name]?.color || "text-slate-400"}`}>{checks[ep.name]?.status || "CHECKING..."}</span>
								</div>
							</div>
						))}
					</div>
				</div>

				{/* WebSocket Status Card */}
				<div className="bg-slate-900/80 backdrop-blur-xl border border-white/10 p-6 rounded-2xl shadow-2xl mb-8">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-3">
							<div className={`w-3 h-3 rounded-full ${wsStatus.status === "CONNECTED" ? "bg-emerald-400" : "bg-red-400"} animate-pulse`}></div>
							<span className="text-slate-300 font-medium">WebSocket Connection</span>
						</div>
						<span className={`font-mono text-sm font-bold ${wsStatus.color}`}>{wsStatus.status}</span>
					</div>
				</div>

				{/* Navigation */}
				<Link href="http://localhost:8000" className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors text-sm">
					← Return to Frontend
				</Link>
			</div>
		</div>
	);
}
